import { useState } from 'react'
import { Box, Text, Button, Input } from '@chakra-ui/react'

import { useLogin } from '@/api/auth'
import IconAbloWhite from '@/components/icons/IconAbloWhite'
import { isValidEmail, isAllowedEmailDomain } from '@/utils/validation'

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const { login, isPending, error: loginError } = useLogin()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address')
      return
    }
    if (!isAllowedEmailDomain(email)) {
      setError('Only @wbd.com and @spacerunners.com emails are allowed')
      return
    }
    setError('')
    login(email, {
      onSuccess: () => {
        window.location.href = `/verify?email=${encodeURIComponent(email)}`
      }
    })
  }

  const isDisabled = !email || isPending

  return (
    <Box
      w="100%"
      maxW="440px"
      px="24px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      position="relative"
      zIndex={1}
    >
      <Box className="animate-fade-in-up">
        <Box className="animate-text-glow-sweep">
          <IconAbloWhite width={180} height={128} />
        </Box>
      </Box>

      <Box className="animate-fade-in-up animate-delay-200">
        <Box
          h="1px"
          w="80px"
          mx="auto"
          mb="12px"
          mt="12px"
          className="animate-shimmer"
          bg="rgba(0, 112, 243, 0.2)"
        />
        <Text color="white" textAlign="center" textStyle="label" mb="48px">
          Where Smart Design Meets IP Security
        </Text>
      </Box>

      <Box
        as="form"
        w="100%"
        onSubmit={handleSubmit}
        className="animate-fade-in-up animate-delay-400"
        bg="rgba(18, 18, 26, 0.5)"
        backdropFilter="blur(16px)"
        borderRadius="20px"
        border="1px solid"
        borderColor="border.subtle"
        p={{ base: '24px', md: '32px' }}
      >
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            if (error) setError('')
          }}
          placeholder="Enter your email"
          h="52px"
          bg="rgba(0, 0, 0, 0.3)"
          border="1px solid"
          borderColor="whiteAlpha.100"
          borderRadius="10px"
          color="text.primary"
          fontSize="14px"
          px="16px"
          mb="16px"
          _placeholder={{ color: 'text.tertiary' }}
          _focus={{
            borderColor: 'accent.500',
            boxShadow:
              '0 0 0 1px rgba(0, 112, 243, 0.5), 0 0 20px rgba(0, 112, 243, 0.1)',
            outline: 'none'
          }}
          _hover={{
            borderColor: 'border.strong'
          }}
          transition="all 0.3s ease"
        />

        {(error || loginError) && (
          <Text
            color="negative"
            mb="12px"
            textStyle="bodySmall"
            textAlign="center"
          >
            {error || loginError?.message}
          </Text>
        )}

        <Button
          type="submit"
          w="100%"
          h="52px"
          textStyle="body"
          fontWeight={500}
          letterSpacing="0.02em"
          borderRadius="10px"
          bg={isDisabled ? 'bg.surface' : 'accent.500'}
          color={isDisabled ? 'text.tertiary' : 'white'}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          border="1px solid"
          borderColor={isDisabled ? 'border.subtle' : 'accent.500'}
          transition="all 0.3s ease"
          className={isDisabled ? '' : 'animate-glow-pulse'}
          _hover={
            isDisabled
              ? {}
              : {
                  bg: 'accent.400',
                  transform: 'translateY(-1px)',
                  boxShadow:
                    '0 0 30px rgba(0, 112, 243, 0.3), 0 4px 20px rgba(0, 0, 0, 0.3)'
                }
          }
          _active={
            isDisabled
              ? {}
              : {
                  transform: 'translateY(0)',
                  boxShadow: '0 0 15px rgba(0, 112, 243, 0.2)'
                }
          }
          onClick={handleSubmit}
        >
          {isPending ? 'Sending...' : 'Continue'}
        </Button>
      </Box>
    </Box>
  )
}
